import React from "react";
import { Col, Row, Space, Tag, Tooltip } from "antd";

import { CheckCircleOutlined, InfoCircleOutlined, LockTwoTone } from "@ant-design/icons";
import styles from "./PremiumFeatures.module.scss";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import PremiumContactUs from "@/components/PremiumFeatureComponents/PremiumContactUs";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import { PREMIUM_IMPACT_METRIC_KEYS } from "@/pages/dashboard/utils/types";

interface DataType {
  name?: string;
  status: boolean;
  tag?: React.ReactNode;
  tooltip?: string;
}

const createRows = (data: DataType) => {
  return (
    <Row key={data.name}>
      <Col span={24}>
        <Space key={data.name}>
          {data.status ? (
            <CheckCircleOutlined style={{ color: "#52C41A" }} />
          ) : (
            <LockTwoTone twoToneColor={["#1890FF", "#CDEAFF"]} />
          )}

          <span>{data.name}</span>
          {data.tag}
          {!!data.tooltip && (
            <Tooltip title={data.tooltip}>
              <InfoCircleOutlined style={{ color: "#8C8C8C" }} />
            </Tooltip>
          )}
        </Space>
      </Col>
    </Row>
  );
};

function ExploreSection({ title, data }: { title: string; data: DataType[] }) {
  return (
    <Row>
      <Col
        span={24}
        className={styles.subHeading}
      >
        <span>{title}</span>
      </Col>
      <Col
        span={24}
        style={{ margin: 0, padding: 0 }}
        className={styles.featuresTable}
      >
        {data.map((item) => createRows(item))}
      </Col>
    </Row>
  );
}

function PremiumExplore({ title, description }: { title?: string; description?: string }) {
  const { data: companySettings } = useCompanySettings();

  const dataOnboarding: DataType[] = [
    {
      name: "Bulk upload order and product and data",
      status: true,
      tooltip: "Quickly upload large volumes of product and order data using Excel or CSV files.",
    },
    {
      name: "Automated supply chain mapping",
      status: true,
      tooltip:
        "Root automatically maps your supply chain based on the data you provide, no need to worry about the complexity of individual LCAs.",
    },
    {
      name: "Estimate production impacts",
      status: true,
      tooltip:
        "Use smart assumptions to estimate production impacts per product when primary data isn’t available.",
    },
    {
      name: "Apply transport routes to multiple orders automatically",
      status: true,
      tooltip:
        "Easily assign a transport route to multiple orders in one go using our route creator wizard.",
    },
    {
      name: "Create proxies based on similar or existing products",
      status: true,
      tooltip:
        "Use another, similar product to fill data gaps. Ideal for early-stage estimates or when primary data is missing.",
    },
    {
      name: "Invite suppliers to collaborate and share data",
      status: !!companySettings?.access.includes("supplier_facility_invite"),
      tooltip:
        "Invite suppliers to collect data like electricity, heat, or water by sharing a public link. You control what’s requested, submitted data will be marked as external in Root.",
    },
  ].sort((a, b) => {
    // show true values first
    if (a.status && !b.status) return -1;
    return 1;
  });

  const dataMatching: DataType[] = [
    {
      name: "Core trusted reference databases (e.g. Ecoinvent, Agri-footprint database)",
      status: true,
      tooltip:
        "Access to reliable, widely used environmental datasets to ensure consistent and accurate impact calculations.",
    },
    {
      name: "Automated impact suggestions for materials",
      status: true,
      tooltip:
        "Root uses a smart matching algorithm to suggest the most relevant impact references for your materials, based on your inputs and trusted databases.",
    },
    {
      name: "Add an Environmental Product Declaration (EPD)",
      status: !!companySettings?.access.includes("epd"),
      tooltip: "Upload official, third-party verified EPDs to improve accuracy. ",
    },
    {
      name: "Add a Custom Impact",
      status: !!companySettings?.access.includes("custom_impact_references"),
      tooltip:
        "Create your own custom impact using reference data from our databases. Ideal when you have primary or supplier-specific data.",
    },
  ].sort((a, b) => {
    // show true values first
    if (a.status && !b.status) return -1;
    return 1;
  });

  const impactInsights: DataType[] = [
    {
      name: "Product-level LCA results",
      status: !!companySettings?.access.includes("products"),
      tooltip:
        "View the full life cycle assessment on a per product-level, including key impact metrics across all phases.",
    },
    {
      name: "impact breakdown per product across life cycle phases",
      status: !!companySettings?.access.includes("products"),
      tooltip:
        "See how different life cycle phases contribute to total impact across all your products.",
    },
    {
      name: "Hotspot analysis for individual products",
      status: !!companySettings?.access.includes("products"),
      tooltip:
        "Identify the highest-impact materials, processes or transport modes within a single product's life cycle.",
    },
    {
      name: "Total company impact distributed across life cycle phases",
      status: !!companySettings?.access.includes("lca"),
      tooltip:
        "Understand your company’s total impact and how it's distributed across each life cycle phase including employee impact.",
    },
    {
      name: "Company-wide hotspot analysis",
      status: !!companySettings?.access.includes("lca"),
      tooltip: "Find the biggest impact drivers across your entire company. ",
    },
    {
      name: "GHG report and export",
      status: !!companySettings?.access.includes("ghg"),
      tooltip:
        "Generate a Greenhouse Gas emissions (GHG) report and export it in a format ready for internal or external reporting. ",
    },
    {
      name: "Access to key environmental metrics",
      status:
        !!companySettings?.access.includes("products") || !!companySettings?.access.includes("lca"),
      tooltip: "Track core indicators such as climate change en environmental costs.",
    },
    {
      name: "Access to more environmental metrics like Biodiversity and Human Health",
      status: PREMIUM_IMPACT_METRIC_KEYS.map((key) => "metric:" + key).some(
        (metric) => !!companySettings?.access.includes(metric)
      ),
      tooltip: "Unlock additional insights, including effects on biodiversity and human health. ",
    },
  ].sort((a, b) => {
    // show true values first
    if (a.status && !b.status) return -1;
    return 1;
  });

  const reduceImpacts: DataType[] = [
    {
      name: "Model alternative scenarios at a product level ",
      status: !!companySettings?.access.includes("product_scenario_builder"),
      tooltip:
        "Create a digital twin of your product to test alternative scenarios. Swap materials or impact matches and instantly see how they affect the product footprint.",
    },
    {
      name: "Model company-wide reduction scenarios",
      status: false,
      tag: <Tag>Coming soon</Tag>,
      tooltip:
        "Build and simulate future scenarios at portfolio level to plan long-term reduction strategies.",
    },
    {
      name: "Analyse interventions based on both cost and environmental impact",
      status: false,
      tag: <Tag>Coming soon</Tag>,
      tooltip:
        "Compare potential actions not just by impact reduction, but also by cost efficiency to prioritise smart trade-offs.",
    },
  ].sort((a, b) => {
    // show true values first
    if (a.status && !b.status) return -1;
    return 1;
  });

  return (
    <div>
      {!!title && (
        <PageHeader
          title={title}
          description={description}
        />
      )}
      <div className={styles.explorePageContainer}>
        <div className={styles.banner}>
          <Row
            justify={"space-evenly"}
            align={"middle"}
            style={{ maxWidth: 900, width: "100%" }}
            gutter={[20, 20]}
          >
            <Col>
              <div className={styles.iconContainer}>
                <LockTwoTone
                  twoToneColor={["#1890FF", "#CDEAFF"]}
                  style={{ fontSize: "16px" }}
                />
              </div>
            </Col>
            <Col>
              <Space direction={"vertical"}>
                <span className={styles.heading}>Explore all premium features</span>
                <span className={styles.description}>
                  Ready for deeper insights? View all features below and contact us for a detailed
                  demo.
                </span>
              </Space>
            </Col>
            <Col>
              <PremiumContactUs />
            </Col>
          </Row>
        </div>
        <div className={styles.features}>
          <Row style={{ maxWidth: 900 }}>
            <Col
              span={24}
              className={styles.mainHeading}
            >
              <span>Features overview</span>
            </Col>
            <Col span={24}>
              <ExploreSection
                title={"1. Data collection"}
                data={dataOnboarding}
              />
              <ExploreSection
                title={"2. Matching"}
                data={dataMatching}
              />
              <ExploreSection
                title={"3. Impact insights"}
                data={impactInsights}
              />
              <ExploreSection
                title={"4. Reduce impact"}
                data={reduceImpacts}
              />
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}

export default PremiumExplore;
